import React from 'react';
import { isWeb } from '../../util/platform';
import { View, StyleSheet, Share, Pressable } from 'react-native'
import { Feather } from '@expo/vector-icons';
import Toast from 'react-native-root-toast';

import CustomText from '../CustomText';
import { webTitleRef } from '../WebTooltip';
import Util from '../../util/util';
import AppStyles from '../../AppStyles';
import { useTranslation } from '../../i18n';

const SHARE_BASE_URL = 'https://monetareport.net/admin-report-details';

/**
 * ReportHeader - Displays the report title with urgent/closed tags
 *
 * Shared between ReportDetails.js and AdminReportDetails.js
 *
 * @param {Object} props
 * @param {string} props.uuid - The report UUID
 * @param {boolean} props.isUrgent - Whether the report is marked urgent
 * @param {boolean} props.isClosed - Whether the report is closed
 * @param {string} [props.organization] - Organization (enables share button when provided)
 * @param {Object} props.webStyles - Web-specific styles from useResponsiveStyles (kept for compatibility)
 * @param {boolean} props.showSeparator - Whether to show separator below header (default: true)
 */
const ReportHeader = ({
  uuid,
  isUrgent = false,
  isClosed = false,
  escalationLevel = 0,
  organization,
  webStyles,
  showSeparator = true,
}) => {
  const { t } = useTranslation('reports');

  const handleShare = async () => {
    const url = `${SHARE_BASE_URL}?uuid=${uuid}&defaultOrganization=${encodeURIComponent(organization)}&noApp=true`;

    try {
      if (isWeb()) {
        await navigator.clipboard.writeText(url);
        Toast.show(t('header.linkCopied'), {
          backgroundColor: AppStyles.colorSet.success,
          position: Toast.positions.BOTTOM,
          duration: 2500,
          textColor: AppStyles.colorSet.whiteColor,
        });
      } else {
        await Share.share({ message: url });
      }
    } catch {
      // Fallback — shouldn't normally fail
    }
  };

  return (
    <View style={styles.container}>
      {/* Title with ID */}
      <View style={styles.titleContainer}>
        <CustomText font="semiBold" size={24} color={AppStyles.colorSet.textPrimary}>
          {t('header.reportTitle', { id: Util.formatUuid(uuid) })}
        </CustomText>
      </View>

      {/* Status Tags + Share */}
      <View style={styles.tagsContainer}>
        {organization && (
          <Pressable
            ref={webTitleRef(t('header.copyLinkLabel'))}
            onPress={handleShare}
            style={({ pressed, hovered, focused }) => [
              styles.shareButton,
              { cursor: 'pointer' },
              (hovered || focused) && styles.shareButtonHovered,
              pressed && { opacity: 0.6 },
            ]}
          >
            <Feather name="link" size={16} color={AppStyles.colorSet.textSecondary} />
          </Pressable>
        )}
        {escalationLevel > 0 && !isClosed && (
          <View style={styles.escalatedTag}>
            <Feather name="arrow-up-circle" size={14} color="#FFF" style={{ marginRight: 4 }} />
            <CustomText font="medium" size={12} color="#FFF">
              {t('header.escalated')}
            </CustomText>
          </View>
        )}
        {isUrgent && !isClosed && (
          <View style={styles.urgentTag}>
            <Feather name="alert-circle" size={14} color="#FFF" style={{ marginRight: 4 }} />
            <CustomText font="medium" size={12} color="#FFF">
              {t('header.urgent')}
            </CustomText>
          </View>
        )}
        {isClosed && (
          <View style={styles.closedTag}>
            <Feather name="check-circle" size={14} color="#FFF" style={{ marginRight: 4 }} />
            <CustomText font="medium" size={12} color="#FFF">
              {t('header.closed')}
            </CustomText>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
    marginBottom: 8,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tagsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  shareButton: {
    padding: 6,
    borderRadius: 8,
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.inputBorder,
  },
  // Web hover/focus feedback for the copy-link button
  shareButtonHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  urgentTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.error,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },
  closedTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.success,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },
  escalatedTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DC2626',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },
});

export default ReportHeader;
